// =============================================================================
// Ocena planu dla modułów organizacji — `moduly_wymagany_plan` (migracja 754).
//
// Po co panelowi. Strażnik na `module_subscriptions` odmawia uruchomienia
// modułu powyżej planu Universe organizacji (`modules.minimalny_plan`). Bez
// tej oceny przycisk „Aktywuj trial” byłby czynny i każde kliknięcie kończyłoby
// się odmową — obietnica na ekranie bez pokrycia w bazie.
//
// TRZY STANY, nie dwa: `wystarcza: true`, `wystarcza: false` z komunikatem
// i „nie wiadomo” (RPC padło albo starszy serwer bez funkcji). Przy „nie
// wiadomo” przycisk zostaje czynny — bazy i tak nie da się obejść, a odmowa
// przyjdzie z tym samym zdaniem w HINT. Wyłączenie przycisku przy awarii
// odczytu odebrałoby trial komuś, komu baza by go dała.
// =============================================================================

import { useEffect, useState } from 'react';

import { supabase } from './supabase';

export interface OcenaPlanuModulu {
  module_id: string;
  modul: string;
  wymagany_plan: string | null;
  wymagany_plan_nazwa: string | null;
  plan_organizacji: string | null;
  plan_organizacji_nazwa: string | null;
  wyjatek: string | null;
  wystarcza: boolean;
  komunikat: string | null;
}

export type OcenyPlanu = Record<string, OcenaPlanuModulu>;

/** Oceny wszystkich modułów organizacji; `null` = nie wiadomo (nie blokuj). */
export async function pobierzOcenyPlanu(organizationId: string): Promise<OcenyPlanu | null> {
  const { data, error } = await supabase.rpc('moduly_wymagany_plan', {
    p_organization_id: organizationId,
  });
  if (error || !data || typeof data !== 'object' || Array.isArray(data)) {
    if (error) console.warn('Nie udało się odczytać wymaganego planu modułów:', error.message);
    return null;
  }
  return data as OcenyPlanu;
}

/**
 * Komunikat blokady planu dla modułu albo `null`, gdy nic nie blokuje (plan
 * wystarcza, moduł nieznany ocenie, ocena niedostępna).
 */
export function blokadaPlanu(
  oceny: OcenyPlanu | null | undefined,
  moduleId: string
): string | null {
  const ocena = oceny?.[moduleId];
  if (!ocena || ocena.wystarcza !== false) return null;
  return (
    ocena.komunikat ??
    `Moduł ${ocena.modul} wymaga wyższego planu Universe. Podnieś plan albo skontaktuj się z opiekunem.`
  );
}

/**
 * Oceny planu dla organizacji, odświeżane przy zmianie organizacji albo
 * `odswiez` (np. listy subskrypcji). `null` = nie wiadomo (brak organizacji,
 * RPC padło) — wtedy nic nie blokuje. Wydzielone z `ModulesPage`, żeby dało
 * się je zmierzyć renderem: mutacja „zawsze `null`" wyłączała blokadę planu
 * na ekranie modułów, a testy czytające źródło jej nie widziały.
 */
export function useOcenyPlanu(
  organizationId: string | null | undefined,
  odswiez?: unknown
): OcenyPlanu | null {
  const [oceny, setOceny] = useState<OcenyPlanu | null>(null);
  useEffect(() => {
    if (!organizationId) {
      setOceny(null);
      return;
    }
    let porzucone = false;
    void pobierzOcenyPlanu(organizationId).then((wynik) => {
      if (!porzucone) setOceny(wynik);
    });
    return () => {
      porzucone = true;
    };
  }, [organizationId, odswiez]);
  return oceny;
}

// -----------------------------------------------------------------------------
// Ocena planu PER PAKIET (migracja 848, rejestr `mandatum_pro_siec_od_planu_start`).
//
// Próg planu Universe zależy od pakietu: Mandatum START kupuje się na planie Free,
// PRO i SIEĆ od planu Start. Okno zakupu pyta o każdy pakiet osobno tą samą
// funkcją, którą kasa odmawia przed płatnością (`modul_wymagany_plan_pakietu`),
// więc przycisk niedostępnego pakietu jest wyłączony ze zdaniem, zamiast kończyć
// się odmową po kliknięciu. Trzy stany jak wyżej: brak oceny = nie blokuj.
// -----------------------------------------------------------------------------

export type OcenyPakietow = Record<string, OcenaPlanuModulu>;

export async function pobierzOcenyPakietow(
  organizationId: string,
  moduleId: string,
  pakiety: readonly string[]
): Promise<OcenyPakietow | null> {
  const wyniki = await Promise.all(
    pakiety.map(async (pakiet) => {
      const { data, error } = await supabase.rpc('modul_wymagany_plan_pakietu', {
        p_organization_id: organizationId,
        p_module_id: moduleId,
        p_prog: pakiet,
      });
      if (error || !data || typeof data !== 'object' || Array.isArray(data)) return null;
      return [pakiet, data as OcenaPlanuModulu] as const;
    })
  );
  const oceny: OcenyPakietow = {};
  for (const w of wyniki) if (w) oceny[w[0]] = w[1];
  return Object.keys(oceny).length > 0 ? oceny : null;
}

/** Zdanie blokady planu dla pakietu albo `null` (wystarcza albo nie wiadomo). */
export function blokadaPakietu(
  oceny: OcenyPakietow | null | undefined,
  pakiet: string | null | undefined
): string | null {
  const ocena = pakiet ? oceny?.[pakiet] : undefined;
  if (!ocena || ocena.wystarcza !== false) return null;
  return (
    ocena.komunikat ??
    `Pakiet wymaga wyższego planu Universe. Podnieś plan albo skontaktuj się z opiekunem.`
  );
}

/** Oceny pakietów modułu z okna zakupu; `null` = nie wiadomo (nie blokuj). */
export function useOcenyPakietow(
  organizationId: string | null | undefined,
  moduleId: string | null | undefined,
  pakiety: readonly string[]
): OcenyPakietow | null {
  const [oceny, setOceny] = useState<OcenyPakietow | null>(null);
  const klucz = pakiety.join(',');
  useEffect(() => {
    if (!organizationId || !moduleId || klucz === '') {
      setOceny(null);
      return;
    }
    let porzucone = false;
    void pobierzOcenyPakietow(organizationId, moduleId, klucz.split(',')).then((wynik) => {
      if (!porzucone) setOceny(wynik);
    });
    return () => {
      porzucone = true;
    };
  }, [organizationId, moduleId, klucz]);
  return oceny;
}
